/**
 * auth.js — Auth endpoint helpers + single-flight refresh primitive.
 *
 * Provides login, logout, and refreshAccessToken. The refresh function
 * uses a module-level promise to coalesce concurrent refresh calls into
 * exactly one POST /auth/refresh per expiry event (single-flight pattern).
 *
 * Import cycle note: api.js will (Task 3) dynamically import refreshAccessToken
 * from here inside its 401 handler. We statically import apiPost — no cycle yet.
 * Never log tokens or passwords.
 */

import { apiPost, ApiError } from './api.js';
import {
  getRefreshToken,
  setSession,
  updateTokens,
  clearSession,
} from './authStore.js';
import { markAttributionSent, pendingAttribution } from './attribution.js';

// ---------------------------------------------------------------------------
// AuthError — signals a terminal auth-dead state (invalid/missing refresh token)
// ---------------------------------------------------------------------------

export class AuthError extends Error {
  /** @param {string} message */
  constructor(message) {
    super(message);
    this.name = 'AuthError';
    /** Callers (router) can check this to redirect to sign-in. */
    this.terminal = true;
  }
}

// ---------------------------------------------------------------------------
// login
// ---------------------------------------------------------------------------

/**
 * POST /auth/login. Stores the session in authStore on success.
 * On failure lets the ApiError propagate — the sign-in UI maps status → copy.
 *
 * Carries the device's first-touch `attribution` until one login or signup succeeds (see
 * lib/attribution.js); omitted after that, or when there is none.
 *
 * @param {{ username: string, password: string }} credentials
 * @returns {Promise<void>}
 */
export async function login({ username, password }) {
  const attribution = pendingAttribution();
  const body = attribution ? { username, password, attribution } : { username, password };
  const res = await apiPost('/auth/login', body, { auth: false });
  if (attribution) markAttributionSent();
  setSession({
    accessToken: res.access_token,
    refreshToken: res.refresh_token,
    username,
  });
}

// ---------------------------------------------------------------------------
// logout
// ---------------------------------------------------------------------------

/**
 * Best-effort POST /auth/logout, then always clears the local session.
 * Never throws. The 204 empty body is handled by swallowing the parse error.
 *
 * @returns {Promise<void>}
 */
export async function logout() {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await apiPost('/auth/logout', { refresh_token: refreshToken }, { auth: false });
    } catch {
      // swallow — logout is idempotent; the local session must be cleared regardless
    }
  }
  clearSession();
}

// ---------------------------------------------------------------------------
// refreshAccessToken — single-flight
// ---------------------------------------------------------------------------

/** @type {Promise<string> | null} */
let inFlight = null;

/**
 * Obtain a new access token by POSTing /auth/refresh with the stored refresh
 * token. Concurrent callers all receive the same in-flight promise — exactly
 * one HTTP request is made per expiry event.
 *
 * Throws AuthError (terminal) when:
 *   - there is no refresh token in storage, or
 *   - the server responds 401.
 *
 * Rethrows ApiError (transient) on network/5xx — the session is NOT cleared.
 *
 * @returns {Promise<string>} the new access token
 */
export function refreshAccessToken() {
  if (inFlight !== null) return inFlight;
  inFlight = doRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** @returns {Promise<string>} */
async function doRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearSession();
    throw new AuthError('No refresh token — session is dead');
  }

  let res;
  try {
    res = await apiPost('/auth/refresh', { refresh_token: refreshToken }, { auth: false });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      clearSession();
      throw new AuthError('Refresh token rejected (401) — session is dead');
    }
    // network/5xx — transient, do NOT clear the session
    throw err;
  }

  // Rotation: both tokens are replaced
  updateTokens({ accessToken: res.access_token, refreshToken: res.refresh_token });
  return res.access_token;
}
